import { action } from "mobx";
import { Observer } from "mobx-react";
import { InputText, InputTextProps } from "primereact/inputtext";
import React from "react";
import { GetDetailsForCandidateResponse } from "../../../api/candidate/getDetailsForCandidate";
import { UpdateCandidateResponse } from "../../../api/candidate/updateCandidate";
import { getLocStore } from "../../../wsLocalization";
import { LocContext } from "../../../wsLocalization/LocContext";
import { invalidateQueries } from "../../../WSUtils/useLatestQuery";
import { concatClassnames } from "../../../WSUtils/util";
import {
  CvEditFieldParams,
  CVEditFieldStore,
  CVEFieldDefFactory,
  cveFieldStore,
  OnRenderCvEditComponent,
} from "./CVEditBaseField";

export interface DefaultCvEditTextFieldDefParams extends CvEditFieldParams {
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  onChange?: InputTextProps["onChange"];
  mutate?: (newValue: string) => Promise<UpdateCandidateResponse>;
  getData: () => GetDetailsForCandidateResponse;
}

export interface CVEditTextFieldStore extends CVEditFieldStore {
  value: string;
}

export const defaultCvEditTextFieldDef: CVEFieldDefFactory<
  CVEditTextFieldStore,
  DefaultCvEditTextFieldDefParams
> = (args) => {
  let {
    fieldName,
    cvEditContext,
    userInfo,
    getValueVersioner,
    queryClient,
    dt,
    id,
    label,
    maxLength,
    minLength,
    required,
    onChange,
    mutate,
    valueValidation: _valueValidation,
    getData,
  } = args;

  const L = getLocStore(userInfo, LocContext.CV);

  const valueValidation = (value: string) => {
    const externalError = !!_valueValidation && _valueValidation(store.value);
    if (externalError) return externalError;

    if (maxLength && value.length > maxLength) {
      return L.id("3b4365d4e510").split("Maks lengde er {0} karakterer", [
        maxLength,
      ]);
    }

    if (minLength && value.length < minLength) {
      return L.id("aa2b9a6de770").split("Minimum lengde er {0} karakterer", [
        minLength,
      ]);
    }

    if (required && value.length === 0) {
      return L.id("dc5d0bde0ee4").loc("Dette feltet er påkrevd");
    }

    return "";
  };

  const store: CVEditTextFieldStore = cveFieldStore({
    value: "",

    get validationError(): string {
      return valueValidation(store.value);
    },
    get showValidationError(): boolean {
      return true;
    },
  });

  const valueVersioner = getValueVersioner(store, 333, true, "fieldName");

  const onNewValue = valueVersioner.onNewValue(() => {
    const data = getData();
    if (!data?.ok) return;

    return {
      valueVersion: data?.valueVersion,
      getVal: () => data[fieldName],
    };
  });

  dt.disposers.push(onNewValue);

  if (!mutate) {
    mutate = (newValue: string) =>
      userInfo.fullAPI.candidateAPI.updateCandidate({
        showSentryDialogOnError: true,
        userInfo: userInfo,
        fields: [
          {
            fieldName: fieldName,
            fieldValue: newValue,
          },
        ],
      });
  }

  const invalidate = () =>
    invalidateQueries(queryClient, ["candidateAPI.getDetailsForCandidate"]);

  if (!onChange) {
    onChange = action((evt) => {
      const newValue = evt.target.value;

      return valueVersioner.onChange({
        newValue,
        mutateBackend: mutate,
        invalidateQuery: invalidate,
      });
    });
  }

  const defaultOnRenderComponent: OnRenderCvEditComponent = (args) => (
    <Observer
      render={() => {
        const defaultProps = args.def.defaultGetOnRenderComponentProps(args);

        return <InputText {...defaultProps} />;
      }}
    />
  );

  const defaultGetOnRenderComponentProps = ({ def }) => ({
    name: def.id,
    id: def.id,
    value: def.store.value,
    onChange,
    className: concatClassnames([
      !!store.visibleValidationError && "p-invalid",
    ]),
  });

  const getLabel = () => L.id(`cvEditFieldLabel[${id}]`).loc(label);

  return {
    ...args,
    hasUnsavedChanges: () => false,
    defaultGetOnRenderComponentProps,
    onChange,
    onNewValue,
    store,
    defaultOnRenderComponent,
    getLabel,
  };
};
